import React, { useState, useEffect } from 'react';
import { useMarket } from '../../hooks/useMarket.jsx';

const CITY_COORDS = {
  'Erfurt': {lat: 50.9787, lon: 11.0328},
  'Jena': {lat: 50.9271, lon: 11.5892},
  'Gera': {lat: 50.8851, lon: 12.0807},
  'Weimar': {lat: 50.9833, lon: 11.3167},
  'Suhl': {lat: 50.6094, lon: 10.6933},
  'Eisenach': {lat: 50.9745, lon: 10.3247},
  'Leipzig': {lat: 51.3396, lon: 12.3713},
  'Halle': {lat: 51.4828, lon: 11.9698}
};

function MenuWeatherWidget() {
  const { currentMarket } = useMarket();
  
  const [weather, setWeather] = useState({
    temp: null,
    condition: 'loading',
    city: currentMarket?.stadt || 'Erfurt',
    icon: '⏳'
  });
  const [time, setTime] = useState(new Date());
  
  // Wetter-Icons basierend auf Condition
  const getWeatherIcon = (condition) => {
    const icons = {
      'clear': '☀️',
      'sunny': '☀️',
      'partly-cloudy': '⛅',
      'cloudy': '☁️',
      'overcast': '☁️',
      'rain': '🌧️',
      'drizzle': '🌦️',
      'snow': '❄️',
      'sleet': '🌨️',
      'thunderstorm': '⛈️',
      'fog': '🌫️',
      'mist': '🌫️',
      'wind': '💨',
      'loading': '⏳',
      'error': '❓'
    };
    return icons[condition] || '🌡️';
  };
  
  // Wetter-Condition aus Code ermitteln (Open-Meteo WMO codes)
  const getConditionFromCode = (code) => {
    if (code === 0) return 'clear';
    if (code === 1 || code === 2) return 'partly-cloudy';
    if (code === 3) return 'cloudy';
    if (code >= 45 && code <= 48) return 'fog';
    if (code >= 51 && code <= 55) return 'drizzle';
    if (code >= 56 && code <= 57) return 'drizzle';
    if (code >= 61 && code <= 65) return 'rain';
    if (code >= 66 && code <= 67) return 'sleet';
    if (code >= 71 && code <= 77) return 'snow';
    if (code >= 80 && code <= 82) return 'rain';
    if (code >= 85 && code <= 86) return 'snow';
    if (code >= 95 && code <= 99) return 'thunderstorm';
    return 'cloudy';
  };
  
  useEffect(() => {
    // Zeit-Update jede Sekunde
    const timeInterval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    
    // Wetter abrufen
    const fetchWeather = async (lat, lon, cityName) => {
      try {
        // Open-Meteo API (kostenlos, kein API-Key nötig)
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&timezone=Europe/Berlin`
        );
        const data = await response.json();
        
        if (data.current) {
          const condition = getConditionFromCode(data.current.weather_code);
          setWeather({
            temp: Math.round(data.current.temperature_2m),
            condition: condition,
            city: cityName,
            icon: getWeatherIcon(condition)
          });
        }
      } catch (error) {
        console.log('Wetter-Fehler:', error);
        setWeather(prev => ({ ...prev, condition: 'error', icon: '❓' }));
      }
    };
    
    // Priorität 1: Standardmarkt aus dem Context
    if (currentMarket && currentMarket.stadt) {
      const city = currentMarket.stadt;
      const coords = CITY_COORDS[city] || CITY_COORDS['Erfurt'];
      fetchWeather(coords.lat, coords.lon, city);
    } else {
      // Priorität 2: Geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            // Reverse Geocoding für Stadtname (optional, vereinfacht)
            try {
              const geoResponse = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
              );
              const geoData = await geoResponse.json();
              const city = geoData.address?.city || geoData.address?.town || geoData.address?.village || 'Unbekannt';
              fetchWeather(latitude, longitude, city);
            } catch {
              fetchWeather(latitude, longitude, 'Standort');
            }
          },
          () => {
            // Fallback: Erfurt
            fetchWeather(50.9787, 11.0328, 'Erfurt');
          },
          { timeout: 5000 }
        );
      } else {
        // Kein Geolocation: Erfurt
        fetchWeather(50.9787, 11.0328, 'Erfurt');
      }
    }
    
    return () => clearInterval(timeInterval);
  }, [currentMarket]);
  
  // Datum formatieren
  const formatDate = () => {
    const options = { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' };
    return time.toLocaleDateString('de-DE', options);
  };
  
  // Zeit formatieren mit Sekunden
  const formatTime = () => {
    return time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  
  // Wetter-Beschreibung aus Condition
  const getWeatherText = (condition) => {
    const texts = {
      'clear': 'Klar',
      'sunny': 'Sonnig',
      'partly-cloudy': 'Teilw. bewölkt',
      'cloudy': 'Bewölkt',
      'overcast': 'Bedeckt',
      'rain': 'Regen',
      'drizzle': 'Nieselregen',
      'snow': 'Schnee',
      'sleet': 'Schneeregen',
      'thunderstorm': 'Gewitter',
      'fog': 'Nebel',
      'mist': 'Nebel',
      'wind': 'Windig',
      'loading': 'Lädt...',
      'error': 'N/A'
    };
    return texts[condition] || 'Wetter';
  };
  
  return (
    <>
      {/* Wetter-Widget: Icon+Zustand links, Temperatur+Stadt rechts - beide vertikal zentriert */}
      <div className="menu-weather-row">
        <div className="menu-weather-icon-left">
          <span className="weather-icon-neon">{weather.icon}</span>
          <span className="weather-condition-under-icon">{getWeatherText(weather.condition)}</span>
        </div>
      </div>

      {/* Temperatur + Stadt oben rechts (gelb markiert in Screenshot) */}
      <div className="menu-weather-top-right" style={{position: 'absolute', right: '12px', top: '12px', zIndex: 9999, pointerEvents: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', textAlign: 'right'}}>
        <div className="menu-weather-corner" style={{display:'flex',flexDirection:'column',alignItems:'flex-end',paddingRight:0,paddingLeft:0,marginLeft:0}}>
          <span className="weather-temp-big">{weather.temp !== null ? `${weather.temp}°` : '--'}</span>
          <span className="weather-city-small">{weather.city}</span>
        </div>
      </div>
    </>
  );
}

export { MenuWeatherWidget };
